// Reading the data of an import (pasted text, a CSV or an Excel sheet) and matching
// its columns to the fields of the profile. Pure functions: no React, no server.

import { parseDelimitedText } from "./generic-data-processing";

export type ImportSeparatorType = "\t" | "," | ";";

/** One field of an import profile, as the column matching sees it. */
export type ImportFieldType = {
  key: string;
  /** Names a column of the file may carry for this field (any language). */
  aliases: string[];
  required: boolean;
  /** Required only when other fields of its group are given (patient 2). */
  conditionalRequired: boolean;
};

export type ColumnMatchType = {
  /** Key of the profile field, or null when the column is left out. */
  target: string | null;
  /** 0-100: how sure the match is. Zero when there is none. */
  confidence: number;
};

const stripAccents = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Lower case, no accents, punctuation as single spaces. */
export const normalizeHeader = (value: string): string =>
  stripAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9#]+/g, " ")
    .trim();

/**
 * The separator of delimited text from its first line, ignoring what is inside
 * quotes. Excel and Sheets paste tabs; CSV exports use "," or ";" (";" wins a tie
 * because Spanish Excel uses it, and a decimal comma is the usual reason for both).
 */
export const detectSeparator = (text: string): ImportSeparatorType => {
  const firstLine = text.replace(/^﻿/, "").split(/\r\n|\n|\r/).find((line) => line.trim() !== "") ?? "";
  const counts: Record<ImportSeparatorType, number> = { "\t": 0, ",": 0, ";": 0 };
  let inQuotes = false;
  for (const char of firstLine) {
    if (char === '"') inQuotes = !inQuotes;
    else if (!inQuotes && char in counts) counts[char as ImportSeparatorType] += 1;
  }
  if (counts["\t"] > 0) return "\t";
  if (counts[";"] > 0 && counts[";"] >= counts[","]) return ";";
  if (counts[","] > 0) return ",";
  return "\t";
};

/** The cells of delimited text, with the separator given or detected. */
export const parseSourceText = (text: string, separator?: ImportSeparatorType): string[][] =>
  parseDelimitedText(text.replace(/^﻿/, ""), separator ?? detectSeparator(text)).map((row) =>
    row.map((cell) => cell.trim()),
  );

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * The cells of a sheet read from an .xlsx file, as text. Dates come out as
 * d/m/yyyy (the format of the import), numbers without exponent, empty cells as "".
 */
export const sheetRowsToText = (rows: unknown[][]): string[][] =>
  rows
    .map((row) =>
      row.map((cell) => {
        if (cell === null || cell === undefined) return "";
        if (cell instanceof Date) {
          // Excel dates carry no zone: they are read as UTC midnight.
          return `${cell.getUTCDate()}/${cell.getUTCMonth() + 1}/${cell.getUTCFullYear()}`;
        }
        if (typeof cell === "number") return Number.isInteger(cell) ? String(cell) : String(Number(cell.toPrecision(15)));
        if (typeof cell === "boolean") return cell ? "Sí" : "No";
        return String(cell).trim();
      }),
    )
    .filter((row) => row.some((cell) => cell !== ""));

/** Whether the bytes are valid UTF-8; CSV files saved by Excel are often Windows-1252. */
export const decodeCsvBytes = (bytes: ArrayBuffer): string => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
};

// The labels of the first column of the downloaded template (and of the example
// files): they mark the instruction rows, which are not data.
const LABEL_HEADER = ["encabezado", "header"];
const LABEL_INFO = ["info"];
const LABEL_SAMPLE = ["datos ejemplo", "sample data"];

const labelKind = (cell: string): "header" | "info" | "sample" | null => {
  const text = normalizeHeader(cell.replace(/=>\s*$/, "").replace(/\(.*\)/, ""));
  if (LABEL_HEADER.some((label) => text === label)) return "header";
  if (LABEL_INFO.some((label) => text === label)) return "info";
  if (LABEL_SAMPLE.some((label) => text === label)) return "sample";
  return null;
};

export type SourceLayoutType = {
  /** The header row, when the data comes in the layout of the template. */
  headers: string[] | null;
  rows: string[][];
  /** True when the template layout was recognised (labels column dropped). */
  fromTemplate: boolean;
};

/**
 * Splits what was read in header and data rows. The sheet of the downloaded template
 * (an "Encabezado =>" row, an "Info =>" row, sample rows and a labels column) is
 * recognised and cleaned: its instruction rows and its first column are dropped, and
 * its header row is returned as the header.
 */
export const readSourceLayout = (rows: string[][]): SourceLayoutType => {
  const kinds = rows.map((row) => labelKind(row[0] ?? ""));
  if (!kinds.includes("header")) return { headers: null, rows, fromTemplate: false };

  const headerIndex = kinds.indexOf("header");
  return {
    headers: rows[headerIndex].slice(1),
    rows: rows.filter((_, index) => kinds[index] === null).map((row) => row.slice(1)),
    fromTemplate: true,
  };
};

// Dice coefficient on bigrams, 0..1 (same idea as the string-similarity package,
// kept local so this module stays free of dependencies).
const similarity = (a: string, b: string): number => {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const bigrams = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const bigram = a.slice(i, i + 2);
    bigrams.set(bigram, (bigrams.get(bigram) ?? 0) + 1);
  }
  let matches = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const bigram = b.slice(i, i + 2);
    const count = bigrams.get(bigram) ?? 0;
    if (count > 0) {
      bigrams.set(bigram, count - 1);
      matches += 1;
    }
  }
  return (2 * matches) / (a.length + b.length - 2);
};

/** The best field a header text names, with 100 for the same words and 80 for a close one. */
export const matchHeader = (header: string, fields: ImportFieldType[]): ColumnMatchType => {
  const text = normalizeHeader(header);
  if (!text) return { target: null, confidence: 0 };

  let best: ColumnMatchType = { target: null, confidence: 0 };
  for (const field of fields) {
    for (const alias of field.aliases) {
      const normalizedAlias = normalizeHeader(alias);
      if (!normalizedAlias) continue;
      const score = text === normalizedAlias ? 1 : similarity(text, normalizedAlias);
      const confidence = score === 1 ? 100 : score >= 0.82 ? 80 : 0;
      if (confidence > best.confidence) best = { target: field.key, confidence };
    }
  }
  return best;
};

/**
 * Whether the first row looks like a header: at least a third of its filled cells
 * name a field of the profile (or one is an exact name when there are only a few).
 */
export const looksLikeHeaderRow = (row: string[] | undefined, fields: ImportFieldType[]): boolean => {
  if (!row) return false;
  const filled = row.filter((cell) => cell.trim() !== "");
  if (filled.length === 0) return false;
  const matched = filled.filter((cell) => matchHeader(cell, fields).target !== null).length;
  return matched >= 1 && matched / filled.length >= 1 / 3;
};

/**
 * One match per column of the file. With a header, by the names; without one, by
 * position when the file has as many columns as the profile (the order of the
 * template). A field is given to a single column: on a tie, the surest one wins.
 */
export const autoMapColumns = (
  headers: string[] | null,
  columnCount: number,
  fields: ImportFieldType[],
): ColumnMatchType[] => {
  const matches: ColumnMatchType[] = Array.from({ length: columnCount }, (_, index) => {
    if (headers) return matchHeader(headers[index] ?? "", fields);
    if (columnCount === fields.length) return { target: fields[index].key, confidence: 60 };
    return { target: null, confidence: 0 };
  });

  const bestByTarget = new Map<string, number>();
  matches.forEach((match, index) => {
    if (!match.target) return;
    const current = bestByTarget.get(match.target);
    if (current === undefined || match.confidence > matches[current].confidence) {
      bestByTarget.set(match.target, index);
    }
  });
  return matches.map((match, index) =>
    match.target && bestByTarget.get(match.target) !== index ? { target: null, confidence: 0 } : match,
  );
};

/** Keys of the fields assigned to more than one column. */
export const duplicateTargets = (targets: Array<string | null>): string[] => {
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  targets.forEach((target) => {
    if (!target) return;
    if (seen.has(target)) duplicated.add(target);
    seen.add(target);
  });
  return Array.from(duplicated);
};

/** Required fields that no column is assigned to. */
export const missingRequiredFields = (
  targets: Array<string | null>,
  fields: ImportFieldType[],
): ImportFieldType[] => {
  const assigned = new Set(targets.filter((target): target is string => target !== null));
  return fields.filter((field) => field.required && !field.conditionalRequired && !assigned.has(field.key));
};

/** The data rows rebuilt with one cell per field of the profile, in its order. */
export const buildProfileRows = (
  rows: string[][],
  targets: Array<string | null>,
  fields: ImportFieldType[],
): string[][] => {
  const columnOf = new Map<string, number>();
  targets.forEach((target, index) => {
    if (target && !columnOf.has(target)) columnOf.set(target, index);
  });
  return rows
    .map((row) =>
      fields.map((field) => {
        const index = columnOf.get(field.key);
        return index === undefined ? "" : (row[index] ?? "").trim();
      }),
    )
    .filter((row) => row.some((cell) => cell !== ""));
};

/** The fields of an import profile, in its order, as the column matching uses them. */
export const getImportFields = (
  columns: Record<
    string,
    {
      required: boolean | null;
      conditionalRequired?: boolean;
      fieldHeader?: Record<string, string>;
    }
  >,
): ImportFieldType[] =>
  Object.entries(columns).map(([key, column]) => ({
    key,
    aliases: [...Object.values(column.fieldHeader ?? {}), key],
    required: Boolean(column.required),
    conditionalRequired: Boolean(column.conditionalRequired),
  }));

export type ResolvedSourceType = {
  /** Names of the columns, or null when the data has no header row. */
  headers: string[] | null;
  dataRows: string[][];
  columnCount: number;
  fromTemplate: boolean;
  /** What looking at the first row suggests (the user can overrule it). */
  suggestedHasHeader: boolean;
};

/**
 * What the data holds once the layout is read: the header row (the one of the
 * template, or the first row when `hasHeader`), the data rows and the column count.
 */
export const resolveSource = (
  rawRows: string[][],
  hasHeader: boolean,
  fields: ImportFieldType[],
): ResolvedSourceType => {
  const layout = readSourceLayout(rawRows);
  const suggestedHasHeader = layout.fromTemplate || looksLikeHeaderRow(rawRows[0], fields);

  let headers: string[] | null;
  let dataRows: string[][];
  if (layout.fromTemplate) {
    headers = layout.headers;
    dataRows = layout.rows;
  } else if (hasHeader && rawRows.length > 0) {
    headers = rawRows[0];
    dataRows = rawRows.slice(1);
  } else {
    headers = null;
    dataRows = rawRows;
  }

  const columnCount = Math.max(headers?.length ?? 0, ...dataRows.map((row) => row.length), 0);
  return { headers, dataRows, columnCount, fromTemplate: layout.fromTemplate, suggestedHasHeader };
};
